import React, { useMemo, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";

import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { PageLayout } from "components/layout";

import {
  useCreateCampaignRouletteMutation,
  useGetDefaultProbabilitiesQuery,
  useLazyValidateCampaignPeriodQuery,
} from "../../../../services/campaign-roulette";
import { 
  useGetStoresOptionsWithDetailsQuery,
  type IStoreOptionWithDetails 
} from "../../../../services/store/select-options";
import {
  STORE_OPTION_VALUES,
  STORE_CODE_VALUES,
} from "../constants/store-options";
import {
  createCampaignRouletteSchema,
  type CreateCampaignRouletteFormData,
} from "../validations/campaign-roulette.validation";
import { transformProbabilitiesToPercentage } from "../utils/probability-utils";
import { calculateTotalProbability } from "../utils/probability-helpers";
import { formatStoreAddress, formatStoreNickname } from "../utils/store-formatters";
import { CampaignType, type IStore } from "../../../../@types/campaign-roulette";

import { BasicInfoCard } from "./components/BasicInfoCard";
import { StoresCard } from "./components/StoresCard";
import { ProbabilitiesCard } from "./components/ProbabilitiesCard";
import { BannersCard } from "./components/BannersCard";
import { RegulationsCard } from "./components/RegulationsCard";
import { useBannerState } from "./hooks/useBannerState";
import { useFormSubmit } from "./hooks/useFormSubmit";
import { useLinkedCampaignSearch } from "../hooks/useLinkedCampaignSearch";

export const CreateCampaignRoulette: React.FC = () => {
  const navigate = useNavigate();
  const [createCampaign, { isLoading: isCreating }] =
    useCreateCampaignRouletteMutation();
  const [validatePeriod] = useLazyValidateCampaignPeriodQuery();
  const {
    data: storeOptions = [],
    isLoading: isLoadingStores,
    isFetching: isFetchingStores,
  } = useGetStoresOptionsWithDetailsQuery();
  const { data: defaultProbabilities = [] } = useGetDefaultProbabilitiesQuery();
  const [storeCodesInput, setStoreCodesInput] = useState("");
  const linkedCampaignSearch = useLinkedCampaignSearch();
  const [selectedLinkedCampaignName, setSelectedLinkedCampaignName] = useState<string | null>(null);

  const form = useForm<CreateCampaignRouletteFormData>({
    resolver: zodResolver(createCampaignRouletteSchema),
    defaultValues: {
      campaignName: "",
      campaignType: CampaignType.ROULETTE,
      startDate: "",
      endDate: "",
      stores: [],
      probabilities: transformProbabilitiesToPercentage(defaultProbabilities),
      regulations: undefined,
      banner3x4: undefined,
      banner16x9: undefined,
      allStoresSelected: false,
    },
  });

  const { fields: probabilityFields, append: appendProbability } =
    useFieldArray({
      control: form.control,
      name: "probabilities",
    });

  const {
    fields: storeFields,
    append: appendStore,
    remove: removeStore,
  } = useFieldArray({
    control: form.control,
    name: "stores",
  });

  const watchProbabilities = form.watch("probabilities");
  const watchBanner3x4 = form.watch("banner3x4");
  const watchBanner16x9 = form.watch("banner16x9");
  const totalProbability = calculateTotalProbability(watchProbabilities);

  const watchCampaignName = form.watch("campaignName");
  const watchStores = form.watch("stores");
  const selectedStoreCodes = (watchStores || [])
    .map((store) => String(store.storeCode))
    .filter(Boolean);
  const allStoresSelected = form.watch("allStoresSelected");

  const storeOptionsMap = useMemo(
    () => new Map(storeOptions.map((store: IStoreOptionWithDetails) => [store.value, store])),
    [storeOptions]
  );

  const comboboxOptions = useMemo(
    () => storeOptions.map((store: IStoreOptionWithDetails) => ({ value: store.value, label: 'Loja ' + store.value })),
    [storeOptions],
  );

  const filteredComboboxOptions = useMemo(
    () =>
      comboboxOptions.filter(
        (store) => !selectedStoreCodes.includes(String(store.value))
      ),
    [comboboxOptions, selectedStoreCodes]
  );

  const storeOptionsWithAll = allStoresSelected
    ? comboboxOptions
    : [
        {
          value: STORE_OPTION_VALUES.ALL_STORES,
          label: "Todas as Lojas",
        },
        ...filteredComboboxOptions,
      ];

  const handleAddStore = (storeCode: string) => {
    if (storeCode === STORE_OPTION_VALUES.ALL_STORES) {
      form.setValue("stores", [
        {
          storeCode: STORE_CODE_VALUES.ALL_STORES_CODE,
          storeName: "",
          nickname: "",
        },
      ]);
      form.setValue("allStoresSelected", true);
      return;
    }

    const store = storeOptionsMap.get(storeCode);
    if (!store) return;

    if (form.getValues("allStoresSelected")) {
      form.setValue("stores", []);
      form.setValue("allStoresSelected", false);
    }

    const storeName = formatStoreAddress(storeCode, storeOptionsMap) || store.label;
    const nickname = formatStoreNickname(storeCode, storeOptionsMap);

    const newStore: IStore = {
      storeCode: store.value,
      storeName,
      nickname,
    };
    appendStore(newStore);
  };

  const handleAddStoresByCodes = () => {
    if (!storeCodesInput.trim()) {
      return;
    }

    if (allStoresSelected) {
      form.setValue("stores", []);
      form.setValue("allStoresSelected", false);
    }

    const codes = Array.from(new Set(
      storeCodesInput
        .split(";")
        .map(code => code.trim())
        .filter(code => code.length > 0)
    ));

    if (codes.length === 0) {
      toast({
        title: "Erro",
        description: "Nenhum código de loja válido foi informado",
        variant: "destructive",
      });
      return;
    }

    const currentStores = form.getValues("stores") || [];
    const existingCodes = currentStores.map(store => String(store.storeCode));
    const newStores: IStore[] = [];
    const notFoundCodes: string[] = [];
    const duplicateCodes: string[] = [];

    codes.forEach(code => {
      if (existingCodes.includes(String(code))) {
        duplicateCodes.push(code);
        return;
      }

      const store = storeOptionsMap.get(code) as IStoreOptionWithDetails | undefined;
      
      if (store) {
        const storeName = formatStoreAddress(code, storeOptionsMap) || store.label;
        const nickname = formatStoreNickname(code, storeOptionsMap);
        newStores.push({
          storeCode: store.value,
          storeName,
          nickname,
        });
        existingCodes.push(String(code));
      } else {
        notFoundCodes.push(code);
      }
    });

    if (newStores.length > 0) {
      newStores.forEach(store => appendStore(store));
      toast({
        title: "Sucesso",
        description: `${newStores.length} loja(s) adicionada(s) com sucesso`,
      });
    }

    if (duplicateCodes.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicateCodes));
      toast({
        title: "Aviso",
        description: `Loja(s) ${uniqueDuplicates.join(", ")} já está(ão) na lista e não foi(ram) adicionada(s) novamente`,
        variant: "destructive",
      });
    }

    if (notFoundCodes.length > 0) {
      const uniqueNotFound = Array.from(new Set(notFoundCodes));
      uniqueNotFound.forEach(code => {
        toast({
          title: "Aviso",
          description: `Loja ${code} não encontrada`,
          variant: "destructive",
        });
      });
    }

    setStoreCodesInput("");
  };

  const handleRemoveStore = (index: number) => {
    removeStore(index);
    if (storeFields.length === 1) {
      form.setValue("allStoresSelected", false);
    }
  };

  const handleResetProbabilities = () => {
    form.setValue(
      "probabilities",
      transformProbabilitiesToPercentage(defaultProbabilities)
    );
  };

  const {
    banner3x4Preview,
    banner16x9Preview,
    handleBanner3x4Change,
    handleBanner16x9Change,
    removeBanner3x4,
    removeBanner16x9,
  } = useBannerState(form.setValue);

  const handleLinkedCampaignSelect = (id: number | null, name: string | null) => {
    form.setValue("linkedCampaignId", id ?? undefined);
    setSelectedLinkedCampaignName(name);
  };

  const { onSubmit } = useFormSubmit({
    createCampaign,
    validatePeriod,
  });

  const isSubmitDisabled =
    isCreating ||
    probabilityFields.length !== 6 ||
    Math.abs(totalProbability - 100) >= 0.01 ||
    !watchBanner3x4 ||
    !watchBanner16x9;

  const getSubmitTitle = () => {
    if (probabilityFields.length !== 6) {
      return "É necessário ter exatamente 6 probabilidades";
    }
    if (Math.abs(totalProbability - 100) >= 0.01) {
      return `A soma das probabilidades deve ser 100%. Atual: ${totalProbability.toFixed(
        2
      )}%`;
    }
    if (!watchBanner3x4 || !watchBanner16x9) {
      return "É necessário enviar os dois banners obrigatórios";
    }
    return "";
  };

  return (
    <PageLayout>
      <div className="flex w-full items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/crm/gestao-campanha-roleta")}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-2xl font-bold">Nova Campanha da Roleta</h1>
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="w-full space-y-6">
        <BasicInfoCard
          register={form.register}
          control={form.control}
          errors={form.formState.errors}
          campaignNameLength={watchCampaignName?.length || 0}
          linkedCampaignSearch={linkedCampaignSearch}
          selectedLinkedCampaignName={selectedLinkedCampaignName}
          onLinkedCampaignSelect={handleLinkedCampaignSelect}
        />

        <StoresCard
          register={form.register}
          setValue={form.setValue}
          storeFields={storeFields}
          storeOptionsWithAll={storeOptionsWithAll}
          allStoresSelected={allStoresSelected || false}
          isLoadingStores={isLoadingStores || isFetchingStores}
          errors={form.formState.errors}
          onAddStore={handleAddStore}
          onRemoveStore={handleRemoveStore}
          storeCodesInput={storeCodesInput}
          setStoreCodesInput={setStoreCodesInput}
          onAddStoresByCodes={handleAddStoresByCodes}
          comboboxOptions={comboboxOptions}
        />

        <ProbabilitiesCard
          register={form.register}
          setValue={form.setValue}
          probabilityFields={probabilityFields}
          appendProbability={appendProbability}
          watchProbabilities={watchProbabilities}
          totalProbability={totalProbability}
          errors={form.formState.errors}
          onResetProbabilities={handleResetProbabilities}
        />

        <BannersCard
          errors={form.formState.errors}
          banner3x4Preview={banner3x4Preview}
          banner16x9Preview={banner16x9Preview}
          onBanner3x4Change={handleBanner3x4Change}
          onBanner16x9Change={handleBanner16x9Change}
          onRemoveBanner3x4={removeBanner3x4}
          onRemoveBanner16x9={removeBanner16x9}
        />

        <RegulationsCard
          setValue={form.setValue}
          errors={form.formState.errors}
        />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/crm/gestao-campanha-roleta")}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitDisabled}
            title={getSubmitTitle()}
          >
            {isCreating ? "Criando..." : "Criar Campanha"}
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};
